import React, { useEffect, useState } from "react";
import { Button, Card, Modal, Placeholder, Spinner, Table } from "react-bootstrap";
import { FiCheckCircle, FiEdit2, FiSlash, FiTrash2 } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { useDebounce } from "@core/hooks/useDebounce";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import NoResult from "@core/components/NoResult";
import { ErrorResponse } from "@core/types/errorResponse";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useEntityContext, usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useFinanceDeductible } from "@features/admin/finance/deductible/hooks/useFinanceDeductible";
import { useFinanceDeductibleAction } from "@features/admin/finance/deductible/hooks/useFinanceDeductibleAction";
import { FinanceDeductible } from "@features/admin/finance/deductible/types/financeDeductible";
import FinanceDeductibleFormModal from "./FinanceDeductibleFormModal";
import DeductibleStatusModal, { DeductibleStatusKind } from "./DeductibleStatusModal";

const DEDUCTIBLE_FILTER_FIELDS: FilterField[] = [
    {
        name: "isActive",
        label: "Status",
        type: "select",
        options: [
            { value: "1", label: "Active" },
            { value: "0", label: "Inactive" },
        ],
    },
];

const DEDUCTIBLE_PERMISSION_CODES = [
    ACTION_CODES.UPDATE_FINANCE_DEDUCTIBLE,
    ACTION_CODES.DISABLE_FINANCE_DEDUCTIBLE,
    ACTION_CODES.ENABLE_FINANCE_DEDUCTIBLE,
    ACTION_CODES.DELETE_FINANCE_DEDUCTIBLE,
];

interface FinanceDeductibleTableProps {
    showPagination?: boolean;
    refreshSignal?: number;
}

const FinanceDeductibleTable: React.FC<FinanceDeductibleTableProps> = ({ showPagination = false, refreshSignal }) => {
    const [searchParams] = useSearchParams();
    const nationId = useScopeId() ?? 0;
    const { context } = useEntityContext();

    const [page, setPage] = useState(() => Number(searchParams.get("page") || "1"));
    const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
    const debouncedSearch = useDebounce(search, 300);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const result: Record<string, any> = {};
        searchParams.forEach((value, key) => {
            if (key !== "q" && key !== "page") result[key] = value;
        });
        return result;
    });

    const [editing, setEditing] = useState<FinanceDeductible | null>(null);
    const [statusTarget, setStatusTarget] = useState<{ deductible: FinanceDeductible; kind: DeductibleStatusKind } | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<FinanceDeductible | null>(null);

    const showError = useApiError();
    const { busy: deleteBusy, doDelete } = useFinanceDeductibleAction();

    const { hasPermission } = usePermissions({
        scopeType: context?.scope,
        scopeId: context?.scopeId,
        actionCodes: DEDUCTIBLE_PERMISSION_CODES,
    });
    const canEdit = hasPermission(ACTION_CODES.UPDATE_FINANCE_DEDUCTIBLE);
    const canDisable = hasPermission(ACTION_CODES.DISABLE_FINANCE_DEDUCTIBLE);
    const canEnable = hasPermission(ACTION_CODES.ENABLE_FINANCE_DEDUCTIBLE);
    const canDelete = hasPermission(ACTION_CODES.DELETE_FINANCE_DEDUCTIBLE);

    useSearchUrlSync({ query: debouncedSearch, filters: activeFilters, page });

    const isActiveFilter =
        activeFilters.isActive === "1" ? true :
        activeFilters.isActive === "0" ? false :
        undefined;

    const { current_page, data, from, last_page, per_page, total, loading, reload } = useFinanceDeductible({
        nationId,
        page,
        ...(debouncedSearch ? { freetext: debouncedSearch } : {}),
        ...(isActiveFilter !== undefined ? { isActive: isActiveFilter } : {}),
    });

    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({ from, page: pageNumber, perPage: per_page, total, currentCount: data.length });

    const didMountRef = React.useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    useEffect(() => { setPage(1); }, [debouncedSearch]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const response = await doDelete(nationId, deleteTarget.deductible_id);
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "");
                setDeleteTarget(null);
                await reload();
            } else {
                showError(response.getError() as ErrorResponse);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    const renderActions = (r: FinanceDeductible) => (
        <TableActions aria-label={`Actions for ${r.name}`}>
            {canEdit && (
                <TableActionButton
                    icon={<FiEdit2 />}
                    label="Edit"
                    primary
                    onClick={() => setEditing(r)}
                    data-testid={`deductible-edit-${r.deductible_id}`}
                />
            )}
            {r.is_active && canDisable && (
                <TableActionButton
                    icon={<FiSlash />}
                    label="Deactivate"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--danger"
                    onClick={() => setStatusTarget({ deductible: r, kind: "deactivate" })}
                    data-testid={`deductible-deactivate-${r.deductible_id}`}
                />
            )}
            {!r.is_active && canEnable && (
                <TableActionButton
                    icon={<FiCheckCircle />}
                    label="Reactivate"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--success"
                    onClick={() => setStatusTarget({ deductible: r, kind: "reactivate" })}
                    data-testid={`deductible-reactivate-${r.deductible_id}`}
                />
            )}
            {!r.is_referenced && canDelete && (
                <TableActionButton
                    icon={<FiTrash2 />}
                    label="Delete"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--danger"
                    onClick={() => setDeleteTarget(r)}
                    data-testid={`deductible-delete-${r.deductible_id}`}
                />
            )}
        </TableActions>
    );

    return (
        <div data-testid="finance-deductible-table">
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search deductibles…"
                    filterFields={DEDUCTIBLE_FILTER_FIELDS}
                    initialQuery={search}
                    onQueryChange={(q) => { setSearch(q); setPage(1); }}
                    onFiltersApply={(f) => { setActiveFilters(f); setPage(1); }}
                    onClear={() => { setSearch(""); setActiveFilters({}); setPage(1); }}
                    loading={loading}
                    activeFilterCount={Object.values(activeFilters).filter(Boolean).length}
                    testId="finance-deductible-search"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="finance-deductible-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive hover size="sm" className="mb-0 align-middle table-striped ntcog-table">
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th style={{ width: 60 }}>#</th>
                                    <th>Name</th>
                                    <th className="d-none d-md-table-cell">Description</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 140 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody
                                className="table-group-divider"
                                style={{ opacity: loading && data.length > 0 ? 0.5 : 1, transition: "opacity 0.2s ease" }}
                            >
                                {loading && data.length === 0 && Array.from({ length: 4 }).map((_, i) => (
                                    <tr key={`ph-${i}`}>
                                        <td><Placeholder xs={3} /></td>
                                        <td><Placeholder xs={6} /></td>
                                        <td className="d-none d-md-table-cell"><Placeholder xs={8} /></td>
                                        <td><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="py-5">
                                            <NoResult
                                                line_1="No finance deductibles found."
                                                line_2="Try adjusting filters or create a new deductible."
                                            />
                                        </td>
                                    </tr>
                                )}
                                {data.map((r) => (
                                    <tr key={r.deductible_id} data-testid={`deductible-row-${r.deductible_id}`} className="bg-white">
                                        <td className="text-muted small">{r.list_order}</td>
                                        <td className="fw-semibold">{r.name}</td>
                                        <td className="small text-muted d-none d-md-table-cell">
                                            <span className="d-inline-block text-truncate" style={{ maxWidth: 360 }} title={r.description ?? undefined}>
                                                {r.description || "—"}
                                            </span>
                                        </td>
                                        <td><StatusPill active={r.is_active} /></td>
                                        <td className="text-end">{renderActions(r)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className="ntcog-card-list d-md-none" data-testid="finance-deductible-cards">
                        {!loading && data.length === 0 && (
                            <NoResult
                                line_1="No finance deductibles found."
                                line_2="Try adjusting filters or create a new deductible."
                            />
                        )}
                        {data.map((r) => (
                            <div key={r.deductible_id} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`deductible-card-${r.deductible_id}`}
                                    ariaLabel={`Deductible ${r.name}`}
                                    title={<span className="fw-semibold">{r.list_order}. {r.name}</span>}
                                    status={<StatusPill active={r.is_active} />}
                                    actions={renderActions(r)}
                                    details={r.description ? [{ label: "Description", value: r.description }] : []}
                                />
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="finance-deductible-pagination"
                    info={`Showing ${start} to ${end} of ${total} deductibles`}
                >
                    <PaginationBar page={pageNumber} totalPages={last_page ?? totalPages} onPage={(p) => setPage(p)} />
                </TablePaginationFooter>
            )}

            <FinanceDeductibleFormModal
                show={editing !== null}
                onHide={() => setEditing(null)}
                deductible={editing}
                onSuccess={reload}
            />

            <DeductibleStatusModal
                deductible={statusTarget?.deductible ?? null}
                kind={statusTarget?.kind ?? "deactivate"}
                onClose={() => setStatusTarget(null)}
                onChanged={reload}
            />

            <Modal show={deleteTarget !== null} onHide={() => { if (!deleteBusy) setDeleteTarget(null); }} centered backdrop="static" data-testid="deductible-delete-modal">
                <Modal.Header closeButton={!deleteBusy}>
                    <Modal.Title>Delete Deductible</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Delete <strong>{deleteTarget?.name}</strong>? It has never been used, so it can be removed. Its name becomes available again.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleteBusy} aria-busy={deleteBusy} data-testid="deductible-delete-confirm-btn">
                        {deleteBusy ? (<><Spinner size="sm" animation="border" className="me-2" />Deleting…</>) : "Yes, delete"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default FinanceDeductibleTable;
